"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";

import type { ReplayPlayerMetrics } from "@/entities/replay-metrics";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { cn } from "@/shared/lib/utils";
import { scoreColor } from "@/shared/lib/score-color";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";

/**
 * KAN-876: выбор игрока для поведенческого профиля (эпик KAN-804).
 *
 * ПОИСК, А НЕ ЧИПЫ — И ЭТО НЕ ВОПРОС ВКУСА. Турнир бывает на 256 участников: строка
 * чипов на такой список превращается в стену, где нужного человека ищут глазами по
 * несколько экранов. Поле ввода сужает список до одного движения.
 *
 * Пустая выдача ПОДПИСАНА. Пустой список читается как «данных нет вовсе», хотя правда
 * обычно проще — ник набран с опечаткой.
 */

/** Порядок ролей в фильтре — привычный порядок линий. */
const ROLE_ORDER = ["TOP", "JUNGLE", "MIDDLE", "BOTTOM", "UTILITY"];

export interface PickerPlayer {
  player: ReplayPlayerMetrics;
  /** Команда игрока — из статистики турнира, метрики её не знают. */
  teamName?: string | null;
}

function playerName(player: ReplayPlayerMetrics): string {
  return (
    (player.displayName ?? "").trim() ||
    (player.riotIdGameName ?? "").trim() ||
    "—"
  );
}

function playerRole(player: ReplayPlayerMetrics): string | null {
  const fromMetric = player.metrics.find((m) => m.peerGroupRole)?.peerGroupRole;
  const role = (fromMetric ?? player.position ?? "").trim();
  return role ? role.toUpperCase() : null;
}

export function ProfilePlayerPicker({
  players,
  selectedUserId,
  onSelect,
}: {
  players: PickerPlayer[];
  selectedUserId?: string | null;
  onSelect: (userId: string) => void;
}) {
  const t = useTranslations("TournamentStats.playerProfile");

  const [query, setQuery] = useState("");
  const [role, setRole] = useState<string | null>(null);

  /** Роли, которые вообще встречаются — фильтр не предлагает пустых кнопок. */
  const roles = useMemo(() => {
    const present = new Set<string>();
    for (const entry of players) {
      const r = playerRole(entry.player);
      if (r) present.add(r);
    }
    return [...present].sort((a, b) => {
      const ai = ROLE_ORDER.indexOf(a);
      const bi = ROLE_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [players]);

  /**
   * Команды известны не всегда. Обещать поиск по команде в подсказке поля, когда
   * команд нет ни у кого, — мелкая ложь, которая стоит человеку нескольких попыток.
   */
  const hasTeams = useMemo(
    () => players.some((entry) => Boolean(entry.teamName?.trim())),
    [players],
  );

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return players
      .filter((entry) => {
        if (role && playerRole(entry.player) !== role) return false;
        if (!needle) return true;

        const haystack = [
          playerName(entry.player),
          entry.player.riotIdGameName ?? "",
          entry.teamName ?? "",
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(needle);
      })
      .sort((a, b) => {
        // Балла нет — вниз, но из списка не убираем: выбрать такого игрока можно,
        // просто профиль покажет сырые значения без оценок.
        const av = a.player.glScore;
        const bv = b.player.glScore;
        if (av == null && bv == null) return playerName(a.player).localeCompare(playerName(b.player));
        if (av == null) return 1;
        if (bv == null) return -1;
        return bv - av;
      });
  }, [players, query, role]);

  return (
    <div className="flex min-w-0 max-w-[310px] flex-1 basis-[250px] flex-col gap-2.5 rounded-[13px] border border-surface-inset-border bg-surface-inset p-3">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={hasTeams ? t("searchPlaceholder") : t("searchPlaceholderNoTeams")}
        aria-label={hasTeams ? t("searchPlaceholder") : t("searchPlaceholderNoTeams")}
        className="w-full rounded-[9px] border border-border bg-background px-[11px] py-[9px] text-[13px] text-foreground outline-none focus:border-primary"
      />

      {roles.length > 1 ? (
        <div className="flex flex-wrap gap-[3px]">
          <button
            type="button"
            onClick={() => setRole(null)}
            aria-pressed={role === null}
            className={cn(
              "flex min-h-[28px] min-w-[32px] items-center justify-center rounded-[7px] border px-2 py-[5px] text-[10px] font-bold uppercase tracking-[0.05em] transition-colors",
              role === null
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:bg-accent",
            )}
          >
            {t("roleAll")}
          </button>
          {roles.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRole(r)}
              title={r}
              aria-label={r}
              aria-pressed={role === r}
              className={cn(
                "flex min-h-[28px] min-w-[32px] items-center justify-center rounded-[7px] border px-2 py-[5px] transition-colors",
                role === r
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:bg-accent",
              )}
            >
              <RoleIcon role={r} size={16} />
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex items-baseline justify-between gap-2 px-0.5 pt-0.5">
        <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
          {t("listCount", { count: visible.length })}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
          {t("scoreLabel")}
        </span>
      </div>

      <div
        role="listbox"
        aria-label={t("listLabel")}
        /* `py-1` — не воздух, а место для рамки. Список прокручивается
           (`overflow-y-auto`), и у выделенной карточки кольцо рисуется по её краю:
           без вертикального отступа верхняя карточка упиралась в край прокрутки и
           её рамка срезалась — на проде это выглядело как «заползает под верхушку». */
        className="-mx-1 flex max-h-[396px] flex-col gap-px overflow-y-auto px-1 py-1"
      >
        {visible.map((entry) => {
          const { player } = entry;
          const id = player.userId ?? "";
          const selected = Boolean(id) && id === selectedUserId;
          const name = playerName(player);
          const score = player.glScore;

          return (
            <button
              key={id || `${player.riotIdGameName}#${player.riotIdTagLine}`}
              type="button"
              role="option"
              disabled={!id}
              aria-selected={selected}
              onClick={() => id && onSelect(id)}
              className={cn(
                "grid grid-cols-[26px_minmax(0,1fr)_34px_30px] items-center gap-2 rounded-[9px] px-[9px] py-[7px] text-left transition-colors",
                selected ? "bg-accent ring-1 ring-primary" : "hover:bg-accent",
                !id && "cursor-default opacity-60",
              )}
            >
              {/* Настоящий аватар; буква — запасной вариант. Метрики отдают
                  `avatarUrl`, и список из одних инициалов был не выбором дизайна,
                  а неиспользованным полем — как и в пьедестале. */}
              <Avatar className="h-[26px] w-[26px]">
                <AvatarImage src={player.avatarUrl ?? undefined} alt={name} />
                <AvatarFallback className="bg-muted text-[10px] font-bold text-muted-foreground">
                  {name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <span className="min-w-0">
                <span className="block truncate text-[13px] font-semibold">{name}</span>
                <span className="block truncate text-[10px] text-muted-foreground">
                  {entry.teamName?.trim() || playerRole(player) || ""}
                </span>
              </span>

              {/* Балла нет — шкала пустая и числа нет. Ноль здесь означал бы
                  «сыграл хуже всех», а правда — «сравнивать не с чем». */}
              <span className="block h-[5px] overflow-hidden rounded-full bg-muted">
                {score != null ? (
                  <span
                    className="block h-full rounded-full"
                    style={{ width: `${score}%`, background: scoreColor(score) }}
                  />
                ) : null}
              </span>

              <span
                className="text-right text-xs font-bold tabular-nums"
                style={score != null ? { color: scoreColor(score) } : undefined}
              >
                {score != null ? Math.round(score) : "—"}
              </span>
            </button>
          );
        })}

        {visible.length === 0 ? (
          <div className="px-2.5 py-[22px] text-center text-xs text-muted-foreground">
            {t("emptyList")}
          </div>
        ) : null}
      </div>
    </div>
  );
}
